import { useTranslation } from 'react-i18next'
import { formatRub } from '@/utils/format'
import type { BillingMetrics as Metrics } from '@/hooks/useBillingAggregation'
import { BILLING_HARD_LIMIT } from '@/hooks/useBillingAggregation'

interface BillingMetricsProps {
  metrics: Metrics
  total: number
  partial: boolean
  isLoadingOrders: boolean
  isLoadingBilling: boolean
}

export function BillingMetrics({
  metrics,
  total,
  partial,
  isLoadingOrders,
  isLoadingBilling,
}: BillingMetricsProps) {
  const { t } = useTranslation()
  const { revenue, payout, margin, ordersCount, settledCount } = metrics

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard
          label={t('billing.metric.revenue')}
          value={isLoadingOrders ? '—' : formatRub(revenue)}
          hint={t('billing.metric.revenueHint')}
        />
        <MetricCard
          label={t('billing.metric.payout')}
          value={isLoadingOrders ? '—' : formatRub(payout)}
          hint={t('billing.metric.payoutHint')}
        />
        <MetricCard
          label={t('billing.metric.margin')}
          value={isLoadingOrders ? '—' : formatRub(margin)}
          hint={t('billing.metric.marginHint')}
        />
        <MetricCard
          label={t('billing.metric.orders')}
          value={isLoadingOrders ? '—' : ordersCount.toString()}
          hint={partial ? t('billing.metric.shownOf', { total }) : undefined}
        />
      </div>
      <div className="min-h-[1.25rem] font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
        {isLoadingOrders ? (
          <span>{t('billing.status.loadingOrders')}</span>
        ) : isLoadingBilling ? (
          <span>
            {t('billing.status.calculating', {
              settled: settledCount,
              count: ordersCount,
            })}
          </span>
        ) : partial ? (
          <span>
            {t('billing.status.partial', {
              count: ordersCount,
              total,
              limit: BILLING_HARD_LIMIT,
            })}
          </span>
        ) : ordersCount > 0 ? (
          <span>{t('billing.status.full', { count: ordersCount })}</span>
        ) : null}
      </div>
    </div>
  )
}

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint?: string
}) {
  return (
    <div className="rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] px-4 py-3">
      <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
        {label}
      </div>
      <div className="mt-1 font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-display)] font-semibold text-[color:var(--stryzh-color-text-primary)]">
        {value}
      </div>
      {hint ? (
        <div className="mt-0.5 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {hint}
        </div>
      ) : null}
    </div>
  )
}
